import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AttendanceKpiResponse } from "@/actions/ssr/attendance/getAttendanceKpiSSR";
import type { AttendanceListResponse } from "@/actions/ssr/attendance/getAttendanceListSSR";
import type { AttendanceOverviewResponse } from "@/actions/ssr/attendance/getAttendanceOverviewSSR";
import type { AttendanceTodayResponse } from "@/actions/ssr/attendance/getAttendanceTodaySSR";

interface AttendanceState {
  attendanceKpi: AttendanceKpiResponse;
  attendanceList: AttendanceListResponse;
  attendanceOverview: AttendanceOverviewResponse;
  attendanceToday: AttendanceTodayResponse;
  listClientFetched: boolean;
  overviewClientFetched: boolean;
  todayClientFetched: boolean;
}

const initialState: AttendanceState = {
  attendanceKpi: {
    present_now: { value: 0, trend: null },
    late_today: { value: 0, trend: null },
    absent: { value: 0, label: "" },
    total_hours: {
      value_minutes: 0,
      value_minutes_paid_aware: 0,
      display: "0h 0m",
      display_paid_aware: "0h 0m",
      trend: null,
    },
  },
  attendanceList: {
    count: 0,
    total_pages: 0,
    current_page: 1,
    results: [],
    counts: {
      all: 0,
      clocked_in: 0,
      break: 0,
      clocked_out: 0,
      not_started: 0,
      absent: 0,
    },
    global_counts: {
      all: 0,
      clocked_in: 0,
      break: 0,
      clocked_out: 0,
      not_started: 0,
      absent: 0,
    },
  },
  attendanceOverview: { present: 0, leave: 0, late: 0, absent: 0, total: 0 },
  attendanceToday: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
    results: [],
  },
  listClientFetched: false,
  overviewClientFetched: false,
  todayClientFetched: false,
};

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    setAttendanceKpi(state, action: PayloadAction<AttendanceKpiResponse>) {
      state.attendanceKpi = action.payload;
    },
    setAttendanceList(state, action: PayloadAction<AttendanceListResponse>) {
      state.listClientFetched = true;
      state.attendanceList = action.payload;
    },
    setAttendanceListFromSSR(
      state,
      action: PayloadAction<AttendanceListResponse>,
    ) {
      if (state.listClientFetched) return;
      state.attendanceList = action.payload;
    },
    setAttendanceOverview(
      state,
      action: PayloadAction<AttendanceOverviewResponse>,
    ) {
      state.overviewClientFetched = true;
      state.attendanceOverview = action.payload;
    },
    setAttendanceOverviewFromSSR(
      state,
      action: PayloadAction<AttendanceOverviewResponse>,
    ) {
      if (state.overviewClientFetched) return;
      state.attendanceOverview = action.payload;
    },
    setAttendanceToday(state, action: PayloadAction<AttendanceTodayResponse>) {
      state.todayClientFetched = true;
      state.attendanceToday = action.payload;
    },
    setAttendanceTodayFromSSR(
      state,
      action: PayloadAction<AttendanceTodayResponse>,
    ) {
      if (state.todayClientFetched) return;
      state.attendanceToday = action.payload;
    },
  },
});

export const {
  setAttendanceKpi,
  setAttendanceList,
  setAttendanceListFromSSR,
  setAttendanceOverview,
  setAttendanceOverviewFromSSR,
  setAttendanceToday,
  setAttendanceTodayFromSSR,
} = attendanceSlice.actions;
export default attendanceSlice.reducer;
